class AudioSync {
  constructor(playerService, transcriptContainer, transcriptType, isMobileDevice) {
    this.playerService = playerService;
    this.transcriptContainer = transcriptContainer;
    this.transcriptType = transcriptType;
    this.activeWord = null;
    this.activeSentence = null;
    this.animationFrameId = null;
    this.isMobileDevice = isMobileDevice;
    this.challenge = false;

    // AudioSync is constructed before the transcript renders, so these
    // caches must fill lazily — a snapshot taken now would stay empty.
    this.words = null;
    this.sentences = null;
  }

  setChallenge(challenge) {
    this.challenge = challenge;
  }

  getWords() {
    if (!this.words || !this.words.length) {
      this.words = document.querySelectorAll('.word');
    }
    return this.words;
  }

  getSentences() {
    if (!this.sentences || !this.sentences.length) {
      this.sentences = document.querySelectorAll('.unactive-sentence, .active-sentence');
    }
    return this.sentences;
  }

  updateActiveSentence(currentTime) {
    let matched = false;

    for (let sentence of this.getSentences()) {
      const sentenceStartTime = parseFloat(sentence.dataset.startTime);
      const sentenceEndTime = parseFloat(sentence.dataset.endTime);

      if (currentTime >= sentenceStartTime && currentTime <= sentenceEndTime) {
        matched = true;

        if (sentence.classList.contains('active-sentence')) {
          break;
        }

        if (this.activeSentence) {
          this.activeSentence.classList.replace('active-sentence', 'unactive-sentence');
          const activeWords = this.activeSentence.querySelectorAll('.word.active-sentence-challenge');
          activeWords.forEach(word => word.classList.remove('active-sentence-challenge'));
        }

        this.activeSentence = sentence;
        sentence.classList.add('active-sentence');

        if (this.challenge) {
          const sentenceWords = this.activeSentence.querySelectorAll('.word');
          sentenceWords.forEach(word => word.classList.add('active-sentence-challenge'));
        }

        if (!this.isElementOutOfView(this.activeSentence)) {
          this.transcriptContainer.scroll({ top: (this.activeSentence.offsetTop - this.transcriptContainer.offsetTop) - 50, behavior: "smooth" });
        }
        break;
      }
    }

    if (!matched && this.activeSentence) {
      // currentTime falls in no sentence — e.g. a dynamically inserted ad.
      // Mirror the JSON path: highlight nothing rather than leaving the
      // previous sentence lit.
      this.activeSentence.classList.replace('active-sentence', 'unactive-sentence');
      const activeWords = this.activeSentence.querySelectorAll('.word.active-sentence-challenge');
      activeWords.forEach(word => word.classList.remove('active-sentence-challenge'));
      this.activeSentence = null;
    }
  }

  updateActiveWord(currentTime) {
    if (this.transcriptContainer.id === 'story-container') {
      this.updateStoryActiveWord(currentTime);
    } else {
      this.updateTranscriptActiveWord(currentTime);
    }
  }

  updateStoryActiveWord(currentTime) {
    if (this.activeWord) {
      this.activeWord.classList.remove('active-word');
    }

    // Find the word that should be active at the current time
    for (const word of this.getWords()) {
      const startTime = parseFloat(word.dataset.startTime);
      const endTime = parseFloat(word.dataset.endTime);

      if (currentTime >= startTime && currentTime <= endTime) {
        this.activeWord = word;
        word.classList.add('active-word');
        
        this.scrollToActiveWord(word);
        return;
      }
    }

    this.activeWord = null;
  }

  updateTranscriptActiveWord(currentTime) {
    const activeSentence = this.getActiveSentence(currentTime);

    if (activeSentence) {
      let wordFound = this.checkWordsInSentence(activeSentence, currentTime);

      if (!wordFound) {
        const nextSentence = this.nextSentenceElement(activeSentence);

        if (nextSentence) {
          this.checkWordsInSentence(nextSentence, currentTime);
        }
      }
    } else {
      // currentTime falls in no sentence — e.g. a dynamically inserted ad.
      // Highlight nothing rather than leaving the previous word lit.
      this.clearActiveHighlight();
    }
  }

  // Walk past non-sentence siblings — a re-timed transcript puts an ad-break
  // divider between segments, and nextElementSibling alone would stop there.
  nextSentenceElement(sentenceElement) {
    let sibling = sentenceElement.nextElementSibling;

    while (sibling &&
           !sibling.classList.contains('unactive-sentence') &&
           !sibling.classList.contains('active-sentence')) {
      sibling = sibling.nextElementSibling;
    }

    return sibling;
  }

  clearActiveHighlight() {
    if (this.activeWord) {
      this.activeWord.style.fontWeight = "400";
      this.activeWord.style.webkitTextStroke = "0px";
      this.activeWord.style.color = "";
      this.activeWord.classList.remove('active-sentence-challenge');
      this.activeWord = null;
    }

    if (this.activeSentence) {
      this.activeSentence.classList.remove('active-sentence');
      this.activeSentence.classList.add('unactive-sentence');
      this.activeSentence = null;
    }
  }

  getActiveSentence(currentTime) {
    let activeSentence = null;

    for (let sentence of this.getSentences()) {
      const sentenceStartTime = parseFloat(sentence.dataset.startTime);
      const sentenceEndTime = parseFloat(sentence.dataset.endTime);

      if (currentTime >= sentenceStartTime && currentTime <= sentenceEndTime) {
        activeSentence = sentence;
        break;
      }
    }

    return activeSentence;
  }

  checkWordsInSentence(sentenceElement, currentTime) {
    const words = sentenceElement.querySelectorAll('.word');
    let wordFound = false;

    for (let word of words) {
      const wordStartTime = parseFloat(word.dataset.startTime);
      const wordEndTime = parseFloat(word.dataset.endTime);

      if (currentTime >= wordStartTime && currentTime <= wordEndTime) {
        if (this.activeWord && this.activeWord.id === word.id) {
          return true;
        }

        if (this.activeWord) {
          this.activeWord.style.fontWeight = "400";
          this.activeWord.style.webkitTextStroke = "0px";

          this.activeWord.classList.remove('active-sentence-challenge');
        }

        if (!this.activeSentence || this.activeSentence.id !== word.parentElement.id) {
          if (this.activeSentence) {
            this.activeSentence.classList.remove('active-sentence');
            this.activeSentence.classList.add('unactive-sentence');
          }
        }

        this.activeWord = word;
        this.activeWord.style.fontWeight = "500";
        this.activeWord.style.webkitTextStroke = "0.5px";
        this.activeSentence = word.parentElement;

        if (this.challenge && word.classList.contains('challenge')) {
          this.activeWord.classList.add('active-sentence-challenge');
        }
        this.activeSentence.classList.add('active-sentence');

        this.scrollToActiveWord(word);
        wordFound = true;
        break;
      } else {
        word.style.fontWeight = "400";
        word.style.webkitTextStroke = "0px";
        word.style.color = "";  // Reset the color when the word is no longer active.
      }
    }

    return wordFound;
  }

  isElementOutOfView(element) {
    const containerRect = this.transcriptContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    let scrollThreshold;

    if (this.isMobileDevice) {
      scrollThreshold = 250;
    } else {
      scrollThreshold = 150;
    }

    if (
      elementRect.bottom < containerRect.top ||
      elementRect.top > (containerRect.bottom - scrollThreshold) ||
      elementRect.right < containerRect.left ||
      elementRect.left > containerRect.right
    ) {
      return true;
    }

    return false;
  }


  scrollToActiveWord(word) {
    if (word && this.isElementOutOfView(word)) {
      this.transcriptContainer.scroll({
        top: (word.offsetTop - this.transcriptContainer.offsetTop) - 50,
        behavior: "smooth"
      });
    }
  }

  handleTimeUpdate() {
    const currentTime = this.playerService.getCurrentTime();

    if (this.transcriptType === 'vtt') {
      this.updateActiveSentence(currentTime);
    } else {
      this.updateActiveWord(currentTime);
    }

    // Continue the animation loop
    this.animationFrameId = requestAnimationFrame(this.handleTimeUpdate.bind(this));
  }

  start() {
    // setupTranscript() can run more than once against the same Plyr instance
    // (initial load, then again when ActionCable reports a fresh transcript),
    // so listeners must be tracked for stop() and never attached twice.
    if (this.onPlay) {
      return;
    }

    this.onPlay = () => {
      this.animationFrameId = requestAnimationFrame(this.handleTimeUpdate.bind(this));
    };
    this.onPauseOrEnded = () => {
      cancelAnimationFrame(this.animationFrameId);
    };

    this.playerService.player.on('play', this.onPlay);
    this.playerService.player.on('pause', this.onPauseOrEnded);
    this.playerService.player.on('ended', this.onPauseOrEnded);

    // start the animation loop if player already playing
    if (this.playerService.isPlaying()) {
      this.animationFrameId = requestAnimationFrame(this.handleTimeUpdate.bind(this));
    }
  }

  stop() {
    cancelAnimationFrame(this.animationFrameId);

    if (this.onPlay) {
      this.playerService.player.off('play', this.onPlay);
      this.playerService.player.off('pause', this.onPauseOrEnded);
      this.playerService.player.off('ended', this.onPauseOrEnded);
      this.onPlay = null;
      this.onPauseOrEnded = null;
    }
  }
}

export default AudioSync;
